import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';
import { QueryKey } from '@/Config/api/queryKey';
import i18n from '@/Config/app/i18n';
import { ImportYoutubeDto } from '@/Domain/api/KnowledgeBase/dto/ImportUrl.dto';
import { useModalStore } from '@/Domain/store/modal.store';

const hasValidVideoId = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname;
    const path = parsed.pathname;
    const searchParams = parsed.searchParams;

    let videoId: string | null = null;

    // youtu.be is the short-link domain for YouTube.
    if (hostname === 'youtu.be') {
      videoId = path.slice(1);
    } else {
      if (path === '/watch') {
        videoId = searchParams.get('v');
      } else if (
        path.startsWith('/embed/') ||
        path.startsWith('/v/') ||
        path.startsWith('/shorts/') ||
        path.startsWith('/live/')
      ) {
        const parts = path.split('/');
        videoId = parts[2] ?? null;
      }
    }

    return !!(videoId && videoId.trim().length > 0);
  } catch {
    return false;
  }
};

const isNotYouTubePlaylist = (url: string): boolean => {
  let path;
  let urlSearchParams;
  try {
    const { searchParams, pathname } = new URL(url);
    path = pathname;
    urlSearchParams = searchParams;
  } catch {
    return false;
  }

  if (path.endsWith('/')) {
    path = path.slice(0, -1);
  }

  if (path !== '/playlist') {
    return true;
  }

  const listParam = urlSearchParams.get('list');
  return !(listParam && listParam.trim().length > 0);
};

const isNotYouTubeChannel = (url: string): boolean => {
  let path;
  try {
    const { pathname } = new URL(url);
    path = pathname;
  } catch {
    return false;
  }

  if (!path) return false;
  if (path.endsWith('/')) path = path.slice(0, -1);

  // Channel URL patterns:
  const isHandleChannel = /^\/@[^/]+(\/.*)?$/.test(path); // /@Google, /@veritasium/about
  const isIdChannel = /^\/channel\/[^/]+(\/.*)?$/.test(path); // /channel/UC_xxx, /channel/UC_xxx/videos
  const isCChannel = /^\/c\/[^/]+(\/.*)?$/.test(path); // /c/Veritasium, /c/Veritasium/videos
  const isUserChannel = /^\/user\/[^/]+(\/.*)?$/.test(path); // /user/SomeName, /user/SomeName/about

  return !(isHandleChannel || isIdChannel || isCChannel || isUserChannel);
};

const isValidYouTubeUrl = (url: string): boolean => {
  try {
    const { hostname } = new URL(url);
    if (!hostname) {
      return false;
    }

    const response =
      hostname === 'youtu.be' || hostname === 'youtube.com' || hostname.endsWith('.youtube.com');
    return response;
  } catch {
    return false;
  }
};

const uploadYoutubeSchema = z.object({
  url: z
    .string()
    .url()
    .min(1)
    .refine(isValidYouTubeUrl, {
      message: i18n.t('knowledge_base:modals.upload_video.youtube.form.errors.youtube_url'),
    })
    .refine(isNotYouTubeChannel, {
      message: i18n.t('knowledge_base:modals.upload_video.youtube.form.errors.youtube_channel'),
    })
    .refine(isNotYouTubePlaylist, {
      message: i18n.t('knowledge_base:modals.upload_video.youtube.form.errors.youtube_playlist'),
    })
    .refine(hasValidVideoId, {
      message: i18n.t('knowledge_base:modals.upload_video.youtube.form.errors.video_id'),
    }),
});

export type UploadYoutubeForm = z.infer<typeof uploadYoutubeSchema>;

export const useUploadYoutubeForm = () => {
  const { t } = useTranslation('knowledge_base', { keyPrefix: 'modals.upload_video.youtube' });
  const { open, closeModal } = useModalStore();

  const form = useForm<UploadYoutubeForm>({
    mode: 'onChange',
    defaultValues: {
      url: '',
    },
    resolver: zodResolver(uploadYoutubeSchema),
  });

  useEffect(() => {
    form.reset({
      url: '',
    });
  }, [open]);

  const uploadYoutubeMutation = useMutation<null, unknown, ImportYoutubeDto>({
    mutationKey: [QueryKey.knowledgeBase.import.youtubeVideo],
  });

  const handleSubmit = (data: UploadYoutubeForm) => {
    closeModal();
    const processUuid = uuidv4();
    toast.loading(t('toast.loading', { url: data.url }), { id: processUuid });
    uploadYoutubeMutation.mutate({
      url: data.url,
      toast_process_uuid: processUuid,
    });
  };
  return { form, handleSubmit };
};
